import { cmToPt } from '../../core/utils/math';
import type { TemplateProfile } from '../types';
import template2Thumbnail from '../../../assets/temp-thumbnails/template-2-thumbnail.jpg';

// ─── Right-column layout constants ───────────────────────────────────────────
//
// Template-2 pages (except first page) use a right-aligned text column:
//   - Left indent: 135 pt  (matches textBlock.ts 'right' layout)
//   - Text block width: 14.09 cm  (right column width)
//   - All graph tables use this same width and indent
//
// Double / half-with-text split:
//   totalWidth = 14 cm
//   separator  = 0.40 cm  (same as Template 1)
//   each col   = (14 - 0.40) / 2 = 6.80 cm  (equal halves)
//
// First-page is unaffected — it uses the cover page layout (no right-col indent).
// ─────────────────────────────────────────────────────────────────────────────

const RIGHT_COL_WIDTH_CM  = 14;
const HALF_COL_WIDTH_CM   = 6;        // Half width graph: 6 cm width
const SEPARATOR_CM        = 0.40;     // Same separator as Template 1
const DOUBLE_COL_CM       = (RIGHT_COL_WIDTH_CM - SEPARATOR_CM) / 2; // 6.80 cm each (equal halves of 14 cm)
const RIGHT_COL_INDENT_PT = 135; // must match textBlock.ts 'right' leftIndent

export const template2Profile: TemplateProfile = {
  id:              'template-2',
  name:            'Asset Allocation',
  version:         '1.0',
  templateSourceKey: 'template-2',
  thumbnail:       template2Thumbnail,

  features: {
    heroBgImages: true,
  },

  colorPalette: {
    exhibitLabel: '000000',
    exhibitDesc:  '000000',
    sourceText:   '000000',
    graphHint:    '000000',
    graphBorder:  undefined,
  },

  typography: {
    fontFamily:     'Arial MT',
    sourceFontSize: 7,
    graphHintSize:  9.5,
    headerFontSize: 9.5,
  },

  layouts: {
    // ── Full-width graph (right column only) ──────────────────────────────
    // Per requirement: full and single half behave identically in template-2
    full: {
      type:          'full',
      // Same total width as the double exhibit (6.81 + 0.40 + 6.81 = 14.02 cm) so
      // both right-aligned tables start at the same left edge.
      colWidth:      cmToPt(6.81) * 2 + cmToPt(SEPARATOR_CM),
      leftIndent:    RIGHT_COL_INDENT_PT,
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.74),
      row3Height:    cmToPt(0.49),
    },

    // ── Half graph — same dimensions as full (per requirement) ────────────
    half: {
      type:          'half',
      colWidth:      cmToPt(6.81),
      leftIndent:    RIGHT_COL_INDENT_PT,
      sepWidth:      undefined,
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.41),
      row3Height:    cmToPt(0.56),
    },

    // ── Double half — two columns fitting within the right column width ───
    double: {
      type:          'double',
      colWidth:      cmToPt(6.81),
      leftIndent:    RIGHT_COL_INDENT_PT,
      sepWidth:      cmToPt(SEPARATOR_CM),         // 0.40 cm
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.41),
      row3Height:    cmToPt(0.56),
    },

    'double-table': {
      type:          'double-table',
      colWidth:      cmToPt(6.81),
      leftIndent:    RIGHT_COL_INDENT_PT,
      sepWidth:      cmToPt(SEPARATOR_CM),
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.41),
      row3Height:    cmToPt(0.56),
    },

    'table-graph': {
      type:          'table-graph',
      colWidth:      cmToPt(6.81),
      leftIndent:    RIGHT_COL_INDENT_PT,
      sepWidth:      cmToPt(SEPARATOR_CM),
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.41),
      row3Height:    cmToPt(0.56),
    },

    // ── Half-with-text — same split as double ─────────────────────────────
    'half-with-text': {
      type:          'half-with-text',
      colWidth:      cmToPt(6.81),
      leftIndent:    RIGHT_COL_INDENT_PT,
      sepWidth:      cmToPt(SEPARATOR_CM),
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.41),
      row3Height:    cmToPt(0.56),
    },

    // ── First-page — cover page layout, no right-column indent ────────────
    'first-page': {
      type:          'first-page',
      colWidth:      cmToPt(13.76),
      leftIndent:    0,                           // no indent on cover page
      row1Height:    cmToPt(0.49),
      row2Height:    cmToPt(4.86),
      row3Height:    cmToPt(0.49),
    },
  },

  supportedLayoutTypes: ['full', 'half', 'double', 'double-table', 'table-graph', 'half-with-text', 'first-page'],

  // ── Table Box layout ──────────────────────────────────────────────────────
  // Fits within the right column: 14 cm wide, 135 pt left indent to align
  // with textBlock 'right' and graph exhibits in this template.
  tableLayout: {
    totalWidth:  cmToPt(RIGHT_COL_WIDTH_CM),  // 14 cm → 397 pt
    leftIndent:  RIGHT_COL_INDENT_PT,          // 135 pt
    row1Height:  cmToPt(0.49),
    row2Height:  cmToPt(2.00),
    row3Height:  cmToPt(0.49),
  },
} satisfies TemplateProfile;